// AMAZON
import i18n from '@/plugins/i18n';
import { isBefore } from 'date-fns';
import { SALES_PLATFORMS } from '@/data/platforms';
import { SETUP_STATE_STATUSES } from '@/data/fivetran';
import { CONNECTION_STATUS } from '@/data/connection-types';
import { ExternalAccount } from '@/types/external-account.types';
import { ExternalAccountState } from '@clearbanc/data-common-types';

// We only want to migrate legacy accounts to Fivetran.
// Newly connected accounts are handled natively by the Shopify team only
// and should continue working normally without being migrated to Fivetran.
const SHOPIFY_NATIVE_CUTOFF_DATE = new Date('2025-01-01');

interface CountryOption {
  key: string;
  value: string;
  name: () => string;
}

interface CountryMapping {
  label: () => string;
  developerID: string;
  domain: string;
}

const AmazonCountryMapping: Record<string, CountryMapping> = {
  NA: {
    label: () => i18n.t('utils.local.northAmerica'),
    developerID: '8356-1075-2313',
    domain: 'com',
  },
  EU: {
    label: () => i18n.t('utils.local.europe'),
    developerID: '5178-4636-8324',
    domain: 'co.uk',
  },
  AU: {
    label: () => i18n.t('utils.local.australia'),
    developerID: '2094-6380-4066',
    domain: 'com.au',
  },
};

// may need to add more endpoints as we expand into new markets
// though i believe this is not likely necessary
const AmazonRegionMapping: Record<string, string> = {
  NA: 'https://sellercentral.amazon.com',
  EU: 'https://sellercentral-europe.amazon.com',
  AU: 'https://sellercentral.amazon.com.au',
};

const AmazonVendorRegionMapping: Record<string, string> = {
  NA: 'https://vendorcentral.amazon.com',
  EU: 'https://vendorcentral-europe.amazon.com',
  AU: 'https://vendorcentral.amazon.com.au',
};
/*
 * takes the region our customer is in (north america, europe, australia/asia)
 */
const getAmazonHostnameByMarketRegion = (market?: string) => {
  const hostname = AmazonRegionMapping[market ?? ''];
  if (!hostname) {
    throw new Error('No hostname associated with Amazon market region', {
      cause: market,
    });
  }

  return hostname;
};

/*
 * takes the region our customer is in (north america, europe, australia/asia)
 */
const getAmazonVendorHostnameByMarketRegion = (market?: string) => {
  const hostname = AmazonVendorRegionMapping[market ?? ''];
  if (!hostname) {
    throw new Error('No hostname associated with Amazon market region', {
      cause: market,
    });
  }

  return hostname;
};

const countryOptions: CountryOption[] = []; // for ui-dropdown
const countryLabels: Record<string, () => string> = {}; // for form-input dropdown
let key = 1;

for (const [countryCode, options] of Object.entries(AmazonCountryMapping)) {
  countryOptions.push({
    key: String(key),
    value: countryCode,
    name: options.label,
  });
  countryLabels[countryCode] = options.label;
  key++;
}

export const amazonCountryOptions = countryOptions;
export const amazonCountryFormOptions = countryLabels;

export function getAmazonCountryMapping(country?: string): CountryMapping {
  return AmazonCountryMapping[country ?? ''] || AmazonCountryMapping.NA;
}

export function isSalesAccountConnected(
  externalAccounts: ExternalAccount[],
): boolean {
  return externalAccounts.some(
    (ext) => ext.platformType === 'sales' && ext.state === 'connected',
  );
}

export function isMarketingAccountConnected(
  externalAccounts: ExternalAccount[],
): boolean {
  return externalAccounts.some(
    (ext) =>
      (ext.platformType === 'marketing' || ext.type === 'facebook') &&
      ext.state === 'connected',
  );
}

export function isAccountingAccountConnected(
  externalAccounts: ExternalAccount[],
): boolean {
  return externalAccounts.some(
    (ext) => ext.platformType === 'accounting' && ext.state === 'connected',
  );
}

// based on the amazon seller central region where our customer is registered,
// return the api hostname associated with that region
export function getAmazonSPAPIConsentUrl(market?: string): string {
  const hostname = getAmazonHostnameByMarketRegion(market);
  return `${hostname}/apps/authorize/consent?state=${market}`;
}

// based on the amazon vendor central region where our customer is registered,
// return the api hostname associated with that region
export function getAmazonSPAPIVendorConsentUrl(market?: string): string {
  const hostname = getAmazonVendorHostnameByMarketRegion(market);
  return `${hostname}/apps/authorize/consent?state=${market}&version=beta`;
}

export function getShopifyAccStatus(
  account?: Pick<ExternalAccount, 'state' | 'connectionDetails'>,
): ExternalAccountState | CONNECTION_STATUS | undefined {
  if (
    account?.state === ExternalAccountState.CONNECTED &&
    (account?.connectionDetails === null ||
      !Object.keys(account.connectionDetails ?? {}).length)
  ) {
    return CONNECTION_STATUS.WARNING;
  } else if (
    account?.connectionDetails &&
    account.connectionDetails?.raw?.status?.setup_state ===
      SETUP_STATE_STATUSES.CONNECTED
  ) {
    return CONNECTION_STATUS.CONNECTED;
  } else if (
    account?.connectionDetails &&
    account.connectionDetails?.raw?.status?.setup_state ===
      SETUP_STATE_STATUSES.INCOMPLETE
  ) {
    return CONNECTION_STATUS.PROCESSING;
  } else if (
    account?.connectionDetails &&
    account.connectionDetails?.raw?.status?.setup_state ===
      SETUP_STATE_STATUSES.BROKEN
  ) {
    return CONNECTION_STATUS.WARNING;
  }

  return account?.state;
}

export function getShopifyNativeAccStatus(
  account?: ExternalAccount,
): ExternalAccountState | CONNECTION_STATUS | undefined {
  if (account?.shopifyNativeState === ExternalAccountState.CONNECTED) {
    return CONNECTION_STATUS.CONNECTED;
  }

  if (
    account?.shopifyNativeState === undefined ||
    account?.shopifyNativeState === ExternalAccountState.DISCONNECTED
  ) {
    if (account?.shopifyFivetranState === ExternalAccountState.CONNECTED) {
      return CONNECTION_STATUS.WARNING;
    } else {
      return CONNECTION_STATUS.DISCONNECTED;
    }
  }

  return CONNECTION_STATUS.DISCONNECTED;
}

export function getExtAccountStatus(
  ext: ExternalAccount,
  showShopifyFivetranTile = false,
): ExternalAccountState | CONNECTION_STATUS | undefined {
  if (ext.platform === SALES_PLATFORMS.SHOPIFY) {
    if (showShopifyFivetranTile) {
      return getShopifyAccStatus(ext);
    } else {
      return getShopifyNativeAccStatus(ext);
    }
  }

  return ext.state;
}

export function isLegacyNativeConnection(account: ExternalAccount): boolean {
  const isLegacyAccount = isBefore(
    new Date(account.createdAt),
    SHOPIFY_NATIVE_CUTOFF_DATE,
  );
  return !account.connectionDetails && isLegacyAccount;
}
